import React from 'react';
import { Button, Upload, Popconfirm, Divider, Form, Input, Checkbox, Collapse, message, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { trackingVitally } from '../../../../helpers/vitally';

const { Panel } = Collapse;

class GeneralEmailSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      imageUrl: '',
      emailFromAddress: '',
      emailLinkToButtonConversion: false,
      enableHtmlEmailTemplates: false,
      migrateHtmlContent: false,
      loading: true,
    };
    this.formRef = React.createRef();
  }

  componentDidMount = async () => {
    await this.fetchEmailSettings();
  };

  fetchEmailSettings = async () => {
    try {
      const response = await axios.get('/api/emailSettings/general');
      if (response.data.status === 'error') {
        const errorMessage = response.data.error;
        this.setState({ loading: false });
        return message.error(errorMessage, 4);
      }
      
      this.setState({
        enableHtmlEmailTemplates: response.data.enableHtmlEmailTemplates,
        emailFromAddress: response.data.emailFromAddress || '',
        imageUrl: response.data.imageUrl || '',
        emailLinkToButtonConversion: response.data.emailLinkToButtonConversion || false,
        migrateHtmlContent: response.data.migrateHtmlContent || false,
        loading: false,
      });
    } catch (err) {
      this.setState({ loading: false });
      return message.error(
        'There was an error loading your email settings. Try again.',
        4
      );
    }
  };

  handleSubmit = async () => {
    try {
      const formValues = await this.formRef.current.validateFields();
      const values = { 
        ...formValues,
      };

      const response = await axios.post('/api/emailSettings/general/update', values);

      if (response.data.status === 'error') {
        return message.error(response.data.error, 4);
      }

      trackingVitally('rr_customeremail_edited');

      message.success('E-Mail settings updated successfully.', 4);
    } catch (err) {
      if (err.errorFields) {
        return message.error(
          'Error saving your email settings. Try again.',
          4
        );
      }

      return message.error(
        'There was an error saving your email settings. Try again.',
        4
      );
    }
  };

  checkEmailImage = (file) => {
    const allowedFileTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!allowedFileTypes.includes(file.type)) {
      message.error(
        'Only .jpg, .jpeg, .png and .svg files smaller than 1 MB allowed.',
        5
      );
      return false;
    }
    if (file.size / 1024 / 1024 > 2) {
      message.error('Only files smaller than 2 MB allowed.', 5);
      return false;
    }
    return true;
  };

  handleImageUpload = async (options) => {
    options.onProgress({ percent: 20 });
    let signedUrl = '';
    let bucketUrl = '';
    try {
      let response = await axios.post('/api/emailSettings/image/upload', {
        fileType: options.file.type,
        fileSize: options.file.size / 1024 / 1024,
      });
      if (response.data.error === 'wrong-size') {
        return message.error('Only files smaller than 2 MB allowed.', 5);
      }
      if (response.data.error === 'wrong-format') {
        return message.error(
          'Only .jpg, .jpeg, .png and .svg files smaller than 1 MB allowed.',
          5
        );
      }
      signedUrl = response.data.signedRequest;
      bucketUrl = response.data.url;
      options.onProgress({ percent: 50 });
    } catch (err) {
      console.log('err', err);
      return message.error('Error preparing upload. Try again.', 4);
    }

    try {
      await axios.put(signedUrl, options.file, {
        headers: { 'Content-Type': options.file.type },
      });
      options.onProgress({ percent: 75 });
      let saveUrl = await axios.post('/api/emailSettings/image/update', {
        imageUrl: bucketUrl,
      });
      if (saveUrl.data.error) {
        return message.error('Error saving image. Try again.', 4);
      }
      message.success('Image uploaded successfully.', 5);
      options.onSuccess({ percent: 100 });
      this.setState({
        imageUrl: bucketUrl,
        ...saveUrl.data,
      });
    } catch (err) {
      options.onError({ percent: 0 });
      return message.error('Error saving inage. Try again.', 4);
    }
  };

  handleDeleteImage = async () => {
    try {
      const response = await axios.post('/api/emailSettings/image/delete');
      if (response.data.error) {
        return message.error('Error deleting current logo. Try again.', 4);
      }
      message.success('Current image deleted successfully', 4);
      this.setState({
        imageUrl: '',
        ...response.data,
      });
    } catch (err) {
      message.error('Error deleting current image. Try again.', 4);
    }
  };

  migrateHtmlContent = async () => {
    try {
      const response = await axios.post('/api/returnSettings/migrateData');
      if (response.status !== 200) {
        return message.error(
          'Error, Unable to migrate html data, Please try again.',
          5
        );
      }
      this.setState({
        migrateHtmlContent: true,
      });
      return message.success('Data migrated successfully.', 5);
    } catch (err) {
      return message.error(
        'Error, Unable to migrate html data, Please try again.',
        5
      );
    }
  };

  onChangeEmailFromAddress = (e) => {
    this.setState({
      emailFromAddress: e.target.value,
    });
    this.formRef.current.setFieldsValue({ emailFromAddress: e.target.value });
  };

  onChangeAttachment = (e) => {
    this.setState({
      emailLinkToButtonConversion: e.target.checked,
    });
  };

  onChangeEnableHtml = (e) => {
    this.setState({
      enableHtmlEmailTemplates: e.target.checked,
    });
  };

  render() {
    const formItemLayout = {
      labelCol: {
        xs: { span: 24 },
        sm: { span: 24 },
      },
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 24 },
      },
    };

    const {
      imageUrl,
      emailFromAddress,
      emailLinkToButtonConversion,
      enableHtmlEmailTemplates,
      loading,
    } = this.state;

    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}>
          <div className="GlobalLoader GlobalLoader--md"></div>
        </div>
      );
    }

    return (
      <Form
        onFinish={this.handleSubmit}
        hideRequiredMark={true}
        ref={this.formRef}
      >
        {enableHtmlEmailTemplates && (
          <React.Fragment>
            <div className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs" style={{ gap: 20, width: '100%' }}>
              <div className="flex-col-md-3 flex-col-sm-12" style={{ padding: 0, width: '100%' }}>
                <h4 data-intercom-target="customer-emails-upload-email-image-heading-notifications-customer-emails">Upload email image header</h4>
                <span>Upload an image to display as a header in your email notifications.</span>
              </div>
              <div className="flex-col-md-8 flex-col-sm-12" style={{ padding: 0, width: '100%' }}>
                <div className="ContentPanel">
                  <div className="flex-row flex-row--noMargin">
                    {imageUrl === '' || imageUrl === null ? (
                      <div className="flex-row flex-row--noMargin flex-middle-xxs flex-between-xxs" style={{ width: '100%', gap: 20 }}>
                        <img src={'/assets/img/all-global-assets/icons/icon-blank-photos--md.svg'} alt="Product Image" style={{ width: 60 }} />
                        <Upload beforeUpload={this.checkEmailImage} customRequest={this.handleImageUpload}>
                          <Button type="ghost" style={{ padding: 'var(--input-padding)' }} data-intercom-target="customer-emails-upload-email-image-button-notifications-customer-emails">Upload image</Button>
                        </Upload>
                      </div>
                    ) : (
                      <div className="flex-row flex-row--noMargin flex-middle-xxs flex-between-xxs" style={{ width: '100%', gap: 20 }}>
                        <img src={imageUrl} style={{ maxHeight: 100, maxWidth: 200 }} />
                        <Popconfirm title="Do you really want to delete the current email image? You can upload a new one later." onConfirm={this.handleDeleteImage} okText="Yes" cancelText="No">
                          <Button type="ghost" style={{ padding: 'var(--input-padding)' }} size="small" data-intercom-target="customer-emails-delete-email-image-button-notifications-customer-emails">Delete?</Button>
                        </Popconfirm>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <Divider />
          </React.Fragment>
        )}
        <div className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs" style={{ gap: 20, width: '100%' }}>
          <div className="flex-col-md-3 flex-col-sm-12" style={{ padding: 0, width: '100%' }}>
            <h4 data-intercom-target="customer-emails-set-default-email-address-heading-notifications-customer-emails">Set default email address</h4>
            <span>This is typically your customer support email and will be used for all outgoing communications.</span>
          </div>
          <div className="flex-col-md-8 flex-col-sm-12" style={{ padding: 0, width: '100%' }}>
            <div className="ContentPanel">
              <div className="flex-row flex-row--noMargin" style={{ width: '100%' }}>
                <h4 data-intercom-target="customer-emails-email-address-heading-notifications-customer-emails">Email address</h4>
                <Form.Item {...formItemLayout} name="emailFromAddress" initialValue={emailFromAddress} rules={[{ required: true, message: 'Please enter a valid email-address.' }]}> 
                  <Input style={{ width: '100%' }} placeholder="Email your customers can reply to" type="email" data-intercom-target="customer-emails-email-address-input-notifications-customer-emails" value={emailFromAddress} onChange={this.onChangeEmailFromAddress} />
                </Form.Item>
              </div>
            </div>
          </div>
        </div>
        <Divider />
        <div className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs" style={{ gap: 20, width: '100%' }}>
          <div className="flex-col-md-3 flex-col-sm-12" style={{ padding: 0, width: '100%' }}>
            <h4 data-intercom-target="customer-emails-enable-html-template-heading-notifications-customer-emails">Enable HTML email template</h4>
            <span>{!this.state.migrateHtmlContent ? 'Click to migrate HTML content' : 'Check this box if you want to enable HTML email template with advanced formatting options.'}</span>
          </div>
          <div className="flex-col-md-8 flex-col-sm-12" style={{ padding: 0, width: '100%' }}>
            <div className="ContentPanel">
              <div className="flex-row flex-row--noMargin flex-middle-xxs" style={{ width: '100%' }}>
                {!this.state.migrateHtmlContent ? (
                  <React.Fragment>
                    <Button
                      data-intercom-target="customer-emails-migrate-html-content-button"
                      type="ghost"
                      onClick={this.migrateHtmlContent}
                      style={{ marginRight: '10px', padding: 'var(--input-padding)' }}
                    >
                      Migrate HTML Content
                    </Button>
                    <Tooltip
                      data-intercom-target="customer-emails-migrate-html-content-tooltip"
                      placement="bottom"
                      title="This is a one time thing, it will migrate all email content to HTML in new columns. By migrating the email content in this way, we ensure that the text and formatting of your emails remain intact without any issues. However, please note that you have the flexibility to modify the email format at any time through the email settings. Once you click on 'Migrate HTML Content,' a checkbox will appear, allowing you to effortlessly switch between the current simple text email format and the new HTML email format."
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </React.Fragment>
                ) : (
                  <Form.Item name="enableHtmlEmailTemplates" initialValue={enableHtmlEmailTemplates} valuePropName="checked">
                    <Checkbox onChange={this.onChangeEnableHtml} data-intercom-target="customer-emails-enable-html-template-checkbox-notifications-customer-emails">Enable HTML template</Checkbox>
                  </Form.Item>
                )}
              </div>
            </div>
          </div>
        </div>
        {enableHtmlEmailTemplates && (
          <React.Fragment>
            <Divider />
            <div className="flex-row flex-row--noMargin flex-top-xxs flex-between-xxs" style={{ gap: 20, width: '100%' }}>
            <div className="flex-col-md-3 flex-col-sm-12" style={{ padding: 0, width: '100%' }}>
              <h4 data-intercom-target="customer-emails-convert-links-heading-notifications-customer-emails">Convert links to buttons</h4>
              <span>Enable this option to transform links in your email template into buttons.</span>
            </div>
            <div className="flex-col-md-8 flex-col-sm-12" style={{ padding: 0, width: '100%' }}>
              <div className="ContentPanel">
                <div className="flex-row flex-row--noMargin" style={{ width: '100%', gap: 20 }}>
                  <Form.Item name="emailLinkToButtonConversion" initialValue={emailLinkToButtonConversion} valuePropName="checked">
                    <Checkbox onChange={this.onChangeAttachment} data-intercom-target="customer-emails-convert-links-checkbox-notifications-customer-emails">Convert links into buttons</Checkbox>
                  </Form.Item>
                  <Collapse>
                    <Panel header="By checking the box, the following variables are going to be displayed as buttons (if applicable):" key="1" data-intercom-target="customer-emails-convert-links-panel-notifications-customer-emails">
                      <p><strong>Shipping-label related</strong></p>
                      <p><span className="ant-tag">{'{LABEL_DOWNLOAD_LINK}'}</span> the download link to the PDF prepaid label, only available if you have automatic prepaid labels enabled</p>
                      <p><span className="ant-tag">{'{LABEL_QR_CODE_DOWNLOAD_LINK}'}</span> the download link to the QR Code label. Only available for USPS on EasyPost and if you have this feature available on your plan</p>
                      <p><span className="ant-tag">{'{LABEL_TRACKING_LINK}'}</span> the tracking link for the prepaid label, only available if you have automatic prepaid labels enabled</p>
                      <p><span className="ant-tag">{'{COMMERCIAL_INVOICE}'}</span> the download link to the commercial invoice for international shipments (if available)</p>
                      <p><span className="ant-tag">{'{MANUAL_LABEL_DOWNLOAD_LINK}'}</span> the download link to the label, only available if you uploaded the prepaid-label for the return</p>
                      <p><span className="ant-tag">{'{MANUAL_LABEL_SECOND_DOWNLOAD_LINK}'}</span> the download link to the second document/label, only available if you uploaded the file in the dashboard</p>
                    </Panel>
                  </Collapse>
                </div>
              </div>
            </div>
          </div>
          </React.Fragment>
        )}
        <div className="flex-row flex-row--noMargin flex-end-xxs u-marginBottom--lg" style={{ width: '100%', marginTop: 30 }}>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ padding: 'var(--input-padding)' }} data-intercom-target="customer-emails-footer-save-changes-button-notifications-customer-emails">Save changes</Button>
          </Form.Item>
        </div>
      </Form>
    );
  }
}

export default GeneralEmailSettings;
